import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Experience, type ExperienceParams } from '@/lib/api';
import { usePropertyContext } from '@/context/PropertyContext';
import { useStayContext } from '@/context/StayContext';

interface UseExperiencesOptions {
  /** Override stay dates from context (useful for testing) */
  stayStartDate?: string;
  stayEndDate?: string;
  /** Minimum guests to check availability for */
  minGuests?: number;
}

interface UseExperiencesReturn {
  experiences: Experience[];
  isLoading: boolean;
  error: string | null;
  params: ExperienceParams;
  /** Refetch experiences with current params */
  refetch: () => void;
  /** Whether results are filtered by stay dates */
  isFilteredByStay: boolean;
}

/**
 * Hook for fetching and managing experiences list
 * 
 * When stay dates are set (via StayContext or options), experiences are
 * filtered to only show those with availability during the guest's stay.
 */
export function useExperiences(options?: UseExperiencesOptions): UseExperiencesReturn {
  const { propertyKey } = usePropertyContext();
  const [searchParams] = useSearchParams();
  const { stayDates, mode, hasValidStayDates } = useStayContext();
  
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Determine effective stay dates (options override context)
  const effectiveStayStart = options?.stayStartDate ?? stayDates.startDate;
  const effectiveStayEnd = options?.stayEndDate ?? stayDates.endDate;
  
  // Only filter by stay dates when in during-stay mode with valid dates
  const shouldFilterByStay = Boolean(
    (mode === 'during-stay' && hasValidStayDates) ||
    (options?.stayStartDate && options?.stayEndDate)
  );

  // Build params from URL and stay context - memoize to avoid unnecessary re-renders
  const params: ExperienceParams = useMemo(() => {
    const baseParams: ExperienceParams = {
      category: searchParams.get('category') || undefined,
      search: searchParams.get('search') || undefined,
      date: searchParams.get('date') || undefined,
    };

    // Add stay date params if filtering is enabled
    if (shouldFilterByStay && effectiveStayStart && effectiveStayEnd) {
      return {
        ...baseParams,
        stayStartDate: effectiveStayStart,
        stayEndDate: effectiveStayEnd,
        minGuests: options?.minGuests,
      };
    }

    return baseParams;
  }, [
    searchParams,
    shouldFilterByStay,
    effectiveStayStart,
    effectiveStayEnd,
    options?.minGuests,
  ]);

  // Create a stable string key for the params to use in useEffect
  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [params]
  );

  // Refetch function
  const refetch = useCallback(() => {
    setFetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    async function fetchExperiences() {
      if (!propertyKey) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await api.getExperiences(propertyKey, params);
        setExperiences(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load experiences');
        setExperiences([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchExperiences();
  }, [propertyKey, paramsKey, params, fetchTrigger]);

  return {
    experiences,
    isLoading,
    error,
    params,
    refetch,
    isFilteredByStay: shouldFilterByStay,
  };
}
